import { CheckCircle2, XCircle } from 'lucide-react'
import type { RuleEngineRunResult } from '../../types/rulesEngine'
import { operatorPhrase } from '../../utils/rulesEngineFields'

type RuleEvalResultsPanelProps = {
  result: RuleEngineRunResult
  ruleSetName?: string
}

export default function RuleEvalResultsPanel({ result, ruleSetName }: RuleEvalResultsPanelProps) {
  const { results, finalOutcome } = result
  const approvedCount = results.filter((item) => item.passed).length
  const rejectedCount = results.length - approvedCount
  const overallApproved = rejectedCount === 0 && results.length > 0

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-bold text-gray-900">Evaluation Results</h3>
        <p className="mt-0.5 text-xs text-gray-500">
          {results.length} rule{results.length === 1 ? '' : 's'} evaluated
          {ruleSetName ? ` for ${ruleSetName}` : ''}
          {' · '}
          {approvedCount} approved, {rejectedCount} not approved
        </p>
      </div>

      <div
        className={`rounded-xl border px-4 py-3 text-sm font-semibold ${
          overallApproved
            ? 'border-teal-200 bg-teal-50 text-teal-800'
            : 'border-rose-200 bg-rose-50 text-rose-800'
        }`}
      >
        {overallApproved ? 'Overall result: Approved' : 'Overall result: Not Approved'}
        {finalOutcome.message ? (
          <span className="mt-0.5 block text-xs font-normal opacity-80">{finalOutcome.message}</span>
        ) : null}
      </div>

      <div className="space-y-3">
        {results.map((item, index) => {
          const approved = item.passed
          return (
            <div
              key={item.rule.id || `${item.rule.name}-${index}`}
              className={`rounded-xl border p-4 ${
                approved ? 'border-teal-200 bg-teal-50/40' : 'border-rose-200 bg-rose-50/40'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {item.fieldLabel || item.rule.name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">{item.rule.name}</p>
                </div>
                <span
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    approved ? 'bg-teal-100 text-teal-800' : 'bg-rose-100 text-rose-800'
                  }`}
                >
                  {approved ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  {approved ? 'Approved' : 'Not Approved'}
                </span>
              </div>

              <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Rule</dt>
                  <dd className="mt-0.5 text-gray-900">
                    {operatorPhrase(item.rule.operator)} {item.expectedValue}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Patient value
                  </dt>
                  <dd className="mt-0.5 text-gray-900">{item.actualValue}</dd>
                </div>
              </dl>

              <p className={`mt-3 text-xs ${approved ? 'text-teal-800' : 'text-rose-800'}`}>
                {item.reason}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
